import { Link, useParams, useNavigate } from 'react-router-dom';
import authApi from '../../api/auth';
import adminApi from '../../api/admin';
import { useAuth } from '../../context/AuthContext';

import React, { useEffect, useState } from 'react';

const DashboardCard = ({ title, children, className }) => {
  return (
    <div className={`p-6 bg-ui-light-card dark:bg-ui-dark-card rounded-lg shadow-md transition-colors ${className}`}>
      <h2 className="mb-4 text-lg font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">{title}</h2>
      {children}
    </div>
  );
};

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roles, setRoles] = useState([]);
  const [isRolesLoading, setIsRolesLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isRoleModalOpen, setIsRoleModalOpen] = useState(false);
  const [resetLinkModal, setResetLinkModal] = useState({ open: false, user: null, link: null, loading: false });
  const [editForm, setEditForm] = useState({
    email: '',
    full_name: '',
    is_active: true
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  
  const usersPerPage = 10;
  const { isSuperuser } = useAuth();
  const { userId } = useParams();
  const navigate = useNavigate();
  
  // Effect to load a specific user if userId is in the URL
  useEffect(() => {
    const loadSingleUser = async () => {
      if (userId) {
        try {
          setIsLoading(true);
          setError(null);
          
          // Fetch the specific user by ID
          const userData = await authApi.getUserById(userId);
          
          // If we get valid user data, display it
          if (userData) {
            // Normalize user data to ensure roles are always an array
            const normalizedUser = { ...userData, roles: userData.roles || [] };
            setUsers([normalizedUser]); // Put in array for consistent display
            setSelectedUser(normalizedUser);
            setIsRoleModalOpen(true); // Open the roles modal
          }
        } catch (err) {
          console.error('Failed to fetch user by ID:', err);
          setError(`Error loading user with ID ${userId}. Please try again.`);
          // Redirect back to the main users page after a short delay
          setTimeout(() => {
            navigate('/admin/users');
          }, 3000);
        } finally {
          setIsLoading(false);
        }
      }
    };
    
    if (userId) {
      loadSingleUser();
    }
  }, [userId, navigate]);
  
  // Helper function to ensure all user objects have a roles array
  const normalizeUserData = (users) => {
    return users.map(user => ({
      ...user,
      // If roles is missing or null, provide an empty array
      roles: user.roles || []
    }));
  };
  
  // Function to fetch roles for a user if they're missing
  const fetchUserRoles = async (userId) => {
    try {
      const roles = await adminApi.getUserRoles(userId);
      return roles;
    } catch (err) {
      console.error(`Error fetching roles for user ${userId}:`, err);
      return []; // Return empty array if there's an error
    }
  };
  
  // Effect to load all users when not viewing a specific user
  useEffect(() => {
    const fetchUsers = async () => {
      // Don't fetch all users if we're viewing a specific user
      if (userId) return;
      
      try {
        setIsLoading(true);
        setIsSearching(false);
        setError(null);
        
        const skip = (currentPage - 1) * usersPerPage;
        const response = await authApi.getUsers(skip, usersPerPage, searchQuery);
        
        // Handle the new paginated response structure
        const { users: usersList, total } = response;
        
        // First normalize user data to ensure roles are always an array
        let normalizedUsers = normalizeUserData(usersList);
        
        // Since the roles might not be included in the list view,
        // we need to fetch roles separately for each user
        const usersWithRoles = await Promise.all(
          normalizedUsers.map(async (user) => {
            // Only fetch roles if they're not already included or empty
            if (!user.roles || user.roles.length === 0) {
              const roles = await fetchUserRoles(user.id);
              return { ...user, roles: roles };
            }
            return user;
          })
        );
        
        setUsers(usersWithRoles);
        setTotalUsers(total);
      } catch (err) {
        console.error('Failed to fetch users:', err);
        setError('Failed to load users. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUsers();
  }, [currentPage, searchQuery, userId]);

  
  // Fetch roles
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        setIsRolesLoading(true);
        const rolesList = await adminApi.getRoles();
        setRoles(rolesList);
      } catch (err) {
        console.error('Failed to fetch roles:', err);
      } finally {
        setIsRolesLoading(false);
      }
    };
    
    fetchRoles();
  }, []);
  
  // Page change handler
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle search input change (no immediate search)
  const handleSearchInputChange = (e) => {
    setSearchInput(e.target.value);
  };

  // Handle search button click
  const handleSearch = () => {
    setSearchQuery(searchInput);
    setIsSearching(true);
    
    // Reset to first page when searching
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    if (currentPage !== 1) {
      setCurrentPage(1);
    }
  };
  
  // Open edit modal for a user
  const openEditModal = (user) => {
    setSelectedUser(user);
    setEditForm({
      email: user.email,
      full_name: user.full_name || '',
      is_active: user.is_active
    });
    setIsEditModalOpen(true);
  };
  
  // Open role management modal
  const openRoleModal = (user) => {
    setSelectedUser(user);
    setIsRoleModalOpen(true);
  };
  
  // Handle input changes in edit form
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Submit edit form
  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const updatedUser = await authApi.updateUser(selectedUser.id, editForm);
      
      // Update the user in the local state
      setUsers(users.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      ));
      
      setIsEditModalOpen(false);
    } catch (err) {
      console.error('Failed to update user:', err);
      // Display error in the form
    }
  };
  
  // Add role to user
  const handleAddRole = async (roleId) => {
    try {
      await adminApi.addUserToRole(selectedUser.id, roleId);
      
      // Refresh the user details to get updated roles
      const updatedUser = await authApi.getUserById(selectedUser.id);
      
      // Update selected user and users list
      setSelectedUser(updatedUser);
      setUsers(users.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      ));
    } catch (err) {
      console.error('Failed to add role to user:', err);
    }
  };
  
  // Remove role from user
  const handleRemoveRole = async (roleId) => {
    try {
      await adminApi.removeUserFromRole(selectedUser.id, roleId);
      
      // Refresh the user details to get updated roles
      const updatedUser = await authApi.getUserById(selectedUser.id);
      
      // Update selected user and users list
      setSelectedUser(updatedUser);
      setUsers(users.map(user => 
        user.id === updatedUser.id ? updatedUser : user
      ));
    } catch (err) {
      console.error('Failed to remove role from user:', err);
    }
  };

  // Generate password reset link
  const handleGenerateResetLink = async (user) => {
    setResetLinkModal({ open: true, user, link: null, loading: true });
    
    try {
      const response = await authApi.generatePasswordResetLink(user.id);
      const fullLink = `${window.location.origin}${response.reset_path}`;
      setResetLinkModal(prev => ({ ...prev, link: fullLink, loading: false }));
    } catch (err) {
      console.error('Failed to generate reset link:', err);
      setResetLinkModal(prev => ({ ...prev, loading: false }));
      // You might want to show an error message here
    }
  };

  // Copy reset link to clipboard
  const copyResetLink = async () => {
    try {
      await navigator.clipboard.writeText(resetLinkModal.link);
      // You might want to show a success message here
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };
  
  // Since we're now doing server-side filtering, we use the users directly
  const filteredUsers = users;
  
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex justify-center">
          <svg className="w-8 h-8 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
        </div>
        <p className="mt-2 text-center text-ui-light-text-muted dark:text-ui-dark-text-muted">Loading users...</p>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="space-y-6">
        <div className="p-4 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-md">
          <p className="font-medium">Error:</p>
          <p>{error}</p>
        </div>
      </div>
    );
  }
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold text-ui-light-text-primary dark:text-ui-dark-text-primary">User Management</h1>
        
        <div className="flex items-center space-x-2">
          <div className="relative flex items-center">
            <input
              type="text"
              placeholder="Search by username..."
              className="px-3 py-2 pr-10 text-sm border border-ui-light-border dark:border-ui-dark-border rounded-l-md focus:outline-none focus:ring-primary-500 focus:border-primary-500 bg-white dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
              value={searchInput}
              onChange={handleSearchInputChange}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            {isSearching && (
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
                <svg className="w-4 h-4 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
            )}
          </div>
          
          <button
            onClick={handleSearch}
            className="px-4 py-2 text-sm font-medium text-white dark:text-ui-dark-text-primary bg-primary-600 dark:bg-primary-700 rounded-r-md hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </button>
          
          {(searchQuery || searchInput) && (
            <button
              onClick={handleClearSearch}
              className="px-3 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      </div>
      
      <DashboardCard title="Users">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-ui-light-border dark:divide-ui-dark-border">
            <thead className="bg-ui-light-hover dark:bg-ui-dark-hover">
              <tr>
                {['Username', 'Email', 'Name', 'Status', 'Roles', 'Actions'].map((header) => (
                  <th 
                    key={header} 
                    scope="col" 
                    className="px-6 py-3 text-xs font-medium tracking-wider text-left text-ui-light-text-muted dark:text-ui-dark-text-muted uppercase"
                  >
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-ui-light-card dark:bg-ui-dark-card divide-y divide-ui-light-border dark:divide-ui-dark-border">
              {filteredUsers.map((user) => (
                <tr key={user.id} className="hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 w-8 h-8">
                        <div className="flex items-center justify-center w-8 h-8 text-white bg-primary-600 dark:bg-primary-700 rounded-full">
                          {user.username.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-ui-light-text-primary dark:text-ui-dark-text-primary">
                          {user.username}
                        </div>
                        <div className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted">
                          {user.is_superuser ? 'Superuser' : user.is_staff ? 'Staff' : 'Regular user'}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                    {user.email}
                  </td>
                  <td className="px-6 py-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                    {user.full_name || '-'}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {user.is_active ? (
                      <span className="inline-flex px-2 text-xs font-semibold leading-5 text-status-success-light-text dark:text-status-success-dark-text bg-status-success-light-bg dark:bg-status-success-dark-bg rounded-full">
                        Active
                      </span>
                    ) : (
                      <span className="inline-flex px-2 text-xs font-semibold leading-5 text-status-error-light-text dark:text-status-error-dark-text bg-status-error-light-bg dark:bg-status-error-dark-bg rounded-full">
                        Inactive
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary whitespace-nowrap">
                    <div className="flex flex-wrap gap-1">
                      {user.roles && user.roles.length > 0 ? (
                        user.roles.map((role) => (
                          <span
                            key={role.id}
                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400"
                          >
                            {role.name}
                          </span>
                        ))
                      ) : (
                        <span className="text-ui-light-text-muted dark:text-ui-dark-text-muted">No roles</span>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm font-medium whitespace-nowrap">
                    <button
                      onClick={() => openEditModal(user)}
                      className="mr-2 text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => openRoleModal(user)}
                      className="mr-2 text-green-600 dark:text-green-400 hover:text-green-700 dark:hover:text-green-300 transition-colors"
                    >
                      Roles
                    </button>
                    <button
                      onClick={() => handleGenerateResetLink(user)}
                      className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300 transition-colors"
                      title="Generate password reset link (expires in 20 minutes)"
                    >
                      Reset Link
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </DashboardCard>
      
      {/* Pagination */}
      {totalUsers > usersPerPage && (
        <div className="flex items-center justify-between px-4 py-3 bg-ui-light-card dark:bg-ui-dark-card border border-ui-light-border dark:border-ui-dark-border rounded-md">
          <div className="flex justify-between flex-1 sm:hidden">
            <button
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage * usersPerPage >= totalUsers}
              className="relative inline-flex items-center px-4 py-2 ml-3 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
            >
              Next
            </button>
          </div>
          <div className="hidden sm:flex sm:items-center sm:justify-between sm:flex-1">
            <div>
              <p className="text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                Showing{" "}
                <span className="font-medium">
                  {(currentPage - 1) * usersPerPage + 1}
                </span>{" "}
                to{" "}
                <span className="font-medium">
                  {Math.min(currentPage * usersPerPage, totalUsers)}
                </span>{" "}
                of <span className="font-medium">{totalUsers}</span> users
              </p>
            </div>
            <div>
              <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-l-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Previous</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
                <span className="relative inline-flex items-center px-4 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border">
                  Page {currentPage}
                </span>
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage * usersPerPage >= totalUsers}
                  className="relative inline-flex items-center px-2 py-2 text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover border border-ui-light-border dark:border-ui-dark-border rounded-r-md hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover disabled:opacity-50 transition-colors"
                >
                  <span className="sr-only">Next</span>
                  <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                    <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      )}
      
      {/* Edit User Modal */}
      {isEditModalOpen && selectedUser && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-ui-light-card dark:bg-ui-dark-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <form onSubmit={handleEditSubmit}>
                <div className="bg-ui-light-card dark:bg-ui-dark-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                  <div className="mb-4">
                    <h3 className="text-lg font-medium leading-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
                      Edit User: {selectedUser.username}
                    </h3>
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="email" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                      Email
                    </label>
                    <input
                      type="email"
                      name="email"
                      id="email"
                      value={editForm.email}
                      onChange={handleInputChange}
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <label htmlFor="full_name" className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="full_name"
                      id="full_name"
                      value={editForm.full_name}
                      onChange={handleInputChange}
                      className="mt-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-ui-light-border dark:border-ui-dark-border rounded-md bg-white dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
                    />
                  </div>
                  
                  <div className="mb-4">
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        name="is_active"
                        id="is_active"
                        checked={editForm.is_active}
                        onChange={handleInputChange}
                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-ui-light-border dark:border-ui-dark-border rounded"
                      />
                      <label htmlFor="is_active" className="ml-2 block text-sm text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">
                        Active account
                      </label>
                    </div>
                  </div>
                </div>
                
                <div className="bg-ui-light-hover dark:bg-ui-dark-hover px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                  <button
                    type="submit"
                    className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 dark:bg-primary-700 text-base font-medium text-white dark:text-ui-dark-text-primary hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Save
                  </button>
                  <button
                    type="button"
                    onClick={() => setIsEditModalOpen(false)}
                    className="mt-3 w-full inline-flex justify-center rounded-md border border-ui-light-border dark:border-ui-dark-border shadow-sm px-4 py-2 bg-white dark:bg-ui-dark-hover text-base font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
      
      {/* Role Management Modal */}
      {isRoleModalOpen && selectedUser && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-ui-light-card dark:bg-ui-dark-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-ui-light-card dark:bg-ui-dark-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium leading-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
                    Manage Roles: {selectedUser.username}
                  </h3>
                </div>
                
                {isRolesLoading ? (
                  <div className="flex justify-center py-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : (
                  <div>
                    <div className="mb-4">
                      <h4 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Current Roles</h4>
                      <div className="mt-2">
                        {selectedUser.roles && selectedUser.roles.length > 0 ? (
                          <div className="flex flex-wrap gap-2">
                            {selectedUser.roles.map((role) => (
                              <div
                                key={role.id}
                                className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-400"
                              >
                                {role.name}
                                <button
                                  type="button"
                                  onClick={() => handleRemoveRole(role.id)}
                                  className="ml-1.5 inline-flex items-center justify-center w-4 h-4 rounded-full text-primary-600 dark:text-primary-400 hover:bg-primary-200 dark:hover:bg-primary-800/50"
                                >
                                  <span className="sr-only">Remove role</span>
                                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-sm text-ui-light-text-muted dark:text-ui-dark-text-muted">No roles assigned</p>
                        )}
                      </div>
                    </div>
                    
                    <div>
                      <h4 className="text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary">Available Roles</h4>
                      <div className="mt-2 grid grid-cols-2 gap-2">
                        {roles
                          .filter(role => !selectedUser.roles || !selectedUser.roles.some(r => r.id === role.id))
                          .map((role) => (
                            <button
                              key={role.id}
                              type="button"
                              onClick={() => handleAddRole(role.id)}
                              className="inline-flex items-center px-2.5 py-1.5 border border-ui-light-border dark:border-ui-dark-border shadow-sm text-xs font-medium rounded text-ui-light-text-secondary dark:text-ui-dark-text-tertiary bg-white dark:bg-ui-dark-hover hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background transition-colors"
                            >
                              <svg className="w-4 h-4 mr-1 text-ui-light-text-muted dark:text-ui-dark-text-muted" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                              </svg>
                              {role.name}
                            </button>
                          ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <div className="bg-ui-light-hover dark:bg-ui-dark-hover px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setIsRoleModalOpen(false)}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 dark:bg-primary-700 text-base font-medium text-white dark:text-ui-dark-text-primary hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Password Reset Link Modal */}
      {resetLinkModal.open && (
        <div className="fixed inset-0 z-10 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>
            
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            
            <div className="inline-block align-bottom bg-ui-light-card dark:bg-ui-dark-card rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-ui-light-card dark:bg-ui-dark-card px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="mb-4">
                  <h3 className="text-lg font-medium leading-6 text-ui-light-text-primary dark:text-ui-dark-text-primary">
                    Password Reset Link for {resetLinkModal.user?.username}
                  </h3>
                </div>
                
                {resetLinkModal.loading ? (
                  <div className="flex justify-center py-4">
                    <svg className="w-6 h-6 text-primary-600 dark:text-primary-400 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  </div>
                ) : resetLinkModal.link ? (
                  <div>
                    <div className="mb-4 p-3 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-md">
                      <div className="flex">
                        <div className="flex-shrink-0">
                          <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                          </svg>
                        </div>
                        <div className="ml-3">
                          <p className="text-sm text-yellow-800 dark:text-yellow-200">
                            This link expires in 20 minutes. Send it to the user immediately.
                          </p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="mb-4">
                      <label className="block text-sm font-medium text-ui-light-text-secondary dark:text-ui-dark-text-tertiary mb-2">
                        Reset Link:
                      </label>
                      <div className="flex">
                        <input
                          type="text"
                          value={resetLinkModal.link}
                          readOnly
                          className="flex-1 focus:ring-primary-500 focus:border-primary-500 block w-full shadow-sm sm:text-sm border-ui-light-border dark:border-ui-dark-border rounded-l-md bg-gray-50 dark:bg-ui-dark-hover text-ui-light-text-primary dark:text-ui-dark-text-primary"
                        />
                        <button
                          onClick={copyResetLink}
                          className="inline-flex items-center px-3 py-2 border border-l-0 border-ui-light-border dark:border-ui-dark-border rounded-r-md bg-ui-light-hover dark:bg-ui-dark-hover text-ui-light-text-secondary dark:text-ui-dark-text-tertiary hover:bg-ui-light-hover dark:hover:bg-ui-dark-hover focus:outline-none focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
                          title="Copy to clipboard"
                        >
                          <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    
                    <div className="text-xs text-ui-light-text-muted dark:text-ui-dark-text-muted">
                      <p>User: {resetLinkModal.user?.email}</p>
                      <p>Link expires in: 20 minutes</p>
                    </div>
                  </div>
                ) : (
                  <p className="text-red-600 dark:text-red-400">Failed to generate reset link. Please try again.</p>
                )}
              </div>
              
              <div className="bg-ui-light-hover dark:bg-ui-dark-hover px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  onClick={() => setResetLinkModal({ open: false, user: null, link: null, loading: false })}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-primary-600 dark:bg-primary-700 text-base font-medium text-white dark:text-ui-dark-text-primary hover:bg-primary-700 dark:hover:bg-primary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 dark:focus:ring-offset-ui-dark-background sm:ml-3 sm:w-auto sm:text-sm transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;